import { useState, useMemo, useEffect } from 'react'
import { TABLE_PAGE_SIZES } from '../constants/table_page_sizes'

const pageSize = (value) => (TABLE_PAGE_SIZES.includes(value) ? value : TABLE_PAGE_SIZES[0])

const toQueryString = (obj, prefix) => {
  return Object.entries(obj).flatMap(([key, value]) => {
    const fullKey = prefix ? `${prefix}[${key}]` : key
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return toQueryString(value, fullKey)
    } else {
      return `${encodeURIComponent(fullKey)}=${encodeURIComponent(value ?? '')}`
    }
  })
}

const useTableFilters = ({
  sortingOptions = null,
  filteringOptions = null,
  enableSearch = true,
  initialSort = { key: 'created_at', direction: 'desc' },
  initialFilter = {},
  initialPagination = { currentPage: 0, rowsPerPage: 25, totalRows: 0 },
  queryExists = false, // if url query string (?...) already present
  persistKey = null,
}) => {
  const getInitialFilters = () => {
    if (persistKey) {
      try {
        const stored = sessionStorage.getItem(`table-filters-${persistKey}`)
        if (stored) {
          const parsed = JSON.parse(stored)
          const hasInitialFilter = Object.keys(initialFilter).length > 0
          return {
            search: parsed.search ?? '',
            filter: hasInitialFilter && !parsed.filterChosen ? initialFilter : (parsed.filter ?? initialFilter),
            filterChosen: Boolean(parsed.filterChosen),
            pagination: {
              ...initialPagination,
              ...parsed.pagination,
              rowsPerPage: pageSize(parsed.pagination?.rowsPerPage),
            },
            sorting: parsed.sorting ?? initialSort,
          }
        }
      } catch (error) {
        console.error('Failed to load table filters from sessionStorage:', error)
      }
    }
    return {
      search: '',
      filter: initialFilter,
      filterChosen: false,
      pagination: { ...initialPagination, rowsPerPage: pageSize(initialPagination.rowsPerPage) },
      sorting: initialSort,
    }
  }
  const [filters, setFilters] = useState(getInitialFilters)

  useEffect(() => {
    if (persistKey) {
      try {
        sessionStorage.setItem(
          `table-filters-${persistKey}`,
          JSON.stringify({
            search: filters.search,
            filter: filters.filter,
            filterChosen: filters.filterChosen,
            sorting: filters.sorting,
            pagination: {
              currentPage: filters.pagination.currentPage,
              rowsPerPage: filters.pagination.rowsPerPage,
              // Don't persist totalRows as it comes from server
            },
          })
        )
      } catch (error) {
        console.error('Failed to save table filters to sessionStorage:', error)
      }
    }
  }, [filters, persistKey])

  const queryString = useMemo(() => {
    const queryFilters = { ...filters }
    delete queryFilters.filterChosen
    const params = toQueryString(queryFilters)
    const prefix = queryExists ? '&' : '?'
    return params.length ? `${prefix}${params.join('&')}` : ''
  }, [filters])

  const searching = enableSearch
    ? {
        search: filters.search,
        onSearch: (search) =>
          setFilters((prev) => ({ ...prev, search, pagination: { ...prev.pagination, currentPage: 0 } })),
        rightIcon: {
          icon: 'close',
          onClick: () => setFilters((prev) => ({ ...prev, search: '' })),
          tooltip: { text: 'Clear search' },
        },
      }
    : null

  const sorting = sortingOptions
    ? {
        options: sortingOptions,
        key: filters.sorting.key,
        sortingKey: filters.sorting.key,
        direction: filters.sorting.direction,
        onSortChange: (sort) =>
          setFilters((prev) => ({
            ...prev,
            sorting: { key: sort.key, direction: sort.direction },
          })),
      }
    : null

  const filtering = filteringOptions
    ? {
        options: filteringOptions,
        filters: filters.filter,
        onFiltersChange: (value) =>
          setFilters((prev) => ({
            ...prev,
            filter: value,
            filterChosen: true,
            pagination: { ...prev.pagination, currentPage: 0 },
          })),
      }
    : null

  const pagination = {
    currentPage: filters.pagination.currentPage,
    rowsPerPage: filters.pagination.rowsPerPage,
    totalRows: filters.pagination.totalRows,
    onPageChange: (currentPage) =>
      setFilters((prev) => ({
        ...prev,
        pagination: { ...prev.pagination, currentPage },
      })),
    onRowsPerPageChange: (rowsPerPage) =>
      setFilters((prev) => ({
        ...prev,
        pagination: { ...prev.pagination, rowsPerPage, currentPage: 0 },
      })),
  }

  return {
    filters,
    queryString,
    searching,
    sorting,
    filtering,
    pagination,
  }
}

export default useTableFilters
